import React, { useContext } from 'react';
import { Form, Button, Col, Container, Row } from "react-bootstrap";
import l10n from "localization/LocalizationData";
import { Gender, GenderKeys } from "models/ResultPacket/Gender";
import { Activity, ActivityKeys } from "models/ResultPacket/Activity";
import { FirstLanguage, FirstLanguageKeys } from "models/ResultPacket/FirstLanguage";
import { Education, EducationKeys } from "models/ResultPacket/Education";
import { FillReason, FillReasonKeys } from "models/ResultPacket/FillReason";
import { RouteComponentProps, Redirect } from 'react-router';
import QuestionnaireContext from 'contexts/QuestionnaireContext';
import { NoAnswer } from 'models/ResultPacket/Preliminary';

export const PreliminaryView = (props: RouteComponentProps<{}>) => {
	const context = useContext(QuestionnaireContext);
	const questionnaire = context.questionnaire;
	const answers = context.answers;

	React.useEffect(() => {
		if(context.lastState !== props.location.pathname)
			context.setLastState(props.location.pathname);
	})

	if (context === undefined || context.token === null || questionnaire === null) {
		return <Redirect to={'/questionnaire'} />
	}

	for (let i = 0; i < questionnaire.questions.length; i++) {
		if (answers[questionnaire.questions[i].id] === undefined) {
			return <Redirect to={`/questionnaire/${questionnaire.id}/question/${i}`} />
		}
	}

	function ageChanged(evt: React.ChangeEvent<HTMLInputElement>) {
		const value = parseInt(evt.currentTarget.value);
		if (value > 0 && value < 125) {
			context.setPreliminary('age', evt.currentTarget.value);
		} else if (value === 0 || isNaN(value)) {
			context.setPreliminary('age', '');
		} else if (evt.currentTarget.value !== '') {
			context.setPreliminary('age', '');
		}
	}

	function genderChanged(evt: React.ChangeEvent<HTMLInputElement>) {
		context.setPreliminary('gender', { value: evt.currentTarget.value });
	}

	function firstLanguageChanged(evt: React.ChangeEvent<HTMLInputElement>) {
		context.setPreliminary('firstlanguage', { value: evt.currentTarget.value });
	}

	function firstLanguageOtherChanged(evt: React.ChangeEvent<HTMLInputElement>) {
		context.setPreliminary('firstlanguage', { value: FirstLanguage[FirstLanguage.other], other: evt.currentTarget.value });
	}

	function activityChanged(evt: React.ChangeEvent<HTMLInputElement>) {
		context.setPreliminary('activity', { value: evt.currentTarget.value });
	}

	function activityOtherChanged(evt: React.ChangeEvent<HTMLInputElement>) {
		context.setPreliminary('activity', { value: Activity[Activity.other], other: evt.currentTarget.value });
	}

	function fillReasonChanged(evt: React.ChangeEvent<HTMLInputElement>) {
		context.setPreliminary('fillreason', { value: evt.currentTarget.value });
	}

	function fillReasonOtherChanged(evt: React.ChangeEvent<HTMLInputElement>) {
		context.setPreliminary('fillreason', { value: FillReason[FillReason.other], other: evt.currentTarget.value });
	}

	function educationChanged(evt: React.ChangeEvent<HTMLInputElement>) {
		context.setPreliminary('education', { value: evt.currentTarget.value });
	}

	function educationOtherChanged(evt: React.ChangeEvent<HTMLInputElement>) {
		context.setPreliminary('education', { value: Education[Education.other], other: evt.currentTarget.value });
	}

	function navigateBack() {
		if (questionnaire?.questions !== undefined) {
			props.history.push(`/questionnaire/${questionnaire?.id}/question/${questionnaire?.questions.length - 1}`);
		}
	}

	function navigateNext() {
		if (questionnaire?.questions !== undefined) {
			props.history.push(`/questionnaire/${questionnaire?.id}/summary`);
		}

	}

	const preliminary = context.preliminary;

	function onSubmit(evt: React.FormEvent<HTMLFormElement>) {
		evt.preventDefault();
	}

	return (
		<Container>
			
				<h1 className='mt-4 mb-4 text-center'>{l10n.preliminary_title}</h1>
			
			<Row>
				<Col xs='12' sm='10' md='8' className='offset-sm-1 offset-md-2'>
					<p>{l10n.preliminary_description}</p>
				</Col>
			</Row>
			<Row className='justify-content-center'>
				<Col xs='12' sm='10' md='8' lg='6'>
					<Form onSubmit={onSubmit} className='preliminary-form'>
						<Form.Group controlId='age'>
							<Form.Label>{l10n.preliminary_age_title}</Form.Label>
							<Form.Control as='input' type='number' min={0} max={125} value={preliminary.age === undefined ? '' : preliminary.age} onChange={ageChanged} />
						</Form.Group>

						<Form.Group controlId='gender'>
							<Form.Label>{l10n.preliminary_gender_title}</Form.Label>
							<Form.Control as='select' name='gender' value={preliminary.gender?.value} onChange={genderChanged}>
								<option hidden value="" label=""></option>
								{
									Object.keys(Gender).filter(x => !(parseInt(x) >= 0)).map(x => {
										return <option key={`gender${x}`} value={x}>{l10n.preliminary_gender_choices[x as GenderKeys]}</option>
									})
								}
								<option value={NoAnswer}>{l10n.preliminary_form_answer_noAnswer}</option>
							</Form.Control>
						</Form.Group>

						<Form.Group controlId='firstlanguage'>
							<Form.Label>{l10n.preliminary_firstLanguage_title}</Form.Label>
							<Form.Control as='select' name='firstLanguage' value={preliminary.firstlanguage?.value?.toString()} onChange={firstLanguageChanged}>
								<option hidden value="" label=""></option>
								{
									Object.keys(FirstLanguage).filter(x => !(parseInt(x) >= 0)).map(x => {
										return <option key={`firstlanguage${x}`} value={x}>{l10n.preliminary_firstLanguage_choices[x as FirstLanguageKeys]}</option>
									})
								}
								<option value={NoAnswer}>{l10n.preliminary_form_answer_noAnswer}</option>
							</Form.Control>
							{
								preliminary.firstlanguage?.value === FirstLanguage[FirstLanguage.other] &&
								<Form.Group as={Form.Row} className='pt-2 pl-1' size='sm'>
									<Form.Label xs='auto' column={true}>
										{l10n.preliminary_form_other_text}
									</Form.Label>
										<Col xs='12' sm={true}>
										<Form.Control as='input' type='text' name='firstlanguageOther' value={preliminary.firstlanguage?.other === undefined ? '' : preliminary.firstlanguage?.other} onChange={firstLanguageOtherChanged} />
									</Col>
								</Form.Group>
							}
						</Form.Group>

						<Form.Group controlId='fillreason'>
							<Form.Label>{l10n.preliminary_fillReason_title }</Form.Label>
							<Form.Control as='select' name='fillreason' value={preliminary.fillreason?.value} onChange={fillReasonChanged} placeholder={'tähän syy miksi täytit'}>
								<option hidden value="" label=""></option>
								{
									Object.keys(FillReason).filter(x => !(parseInt(x) >= 0)).map(x => {
										return <option key={`fillreason${x}`} value={x}>{l10n.preliminary_fillReason[x as FillReasonKeys]}</option>
									})
								}
								<option value={NoAnswer}>{l10n.preliminary_form_answer_noAnswer}</option>
							</Form.Control>

							{
								preliminary.fillreason?.value === FillReason[FillReason.other] &&
								<Form.Group as={Form.Row} className='pt-2 pl-1' size='sm'>
									<Form.Label xs='auto' column={true}>
										{l10n.preliminary_form_other_text}
									</Form.Label>
										<Col xs='12' sm={true}>
										<Form.Control as='input' type='text' name='genderOther' value={preliminary.fillreason?.other === undefined ? '' : preliminary.fillreason?.other} onChange={fillReasonOtherChanged} />
									</Col>
								</Form.Group>
							}
						</Form.Group>

						<Form.Group controlId='activity'>
							<Form.Label>{l10n.preliminary_activity_title}</Form.Label>
							<Form.Control as='select' name='activity' value={preliminary.activity?.value} onChange={activityChanged}>
								<option hidden value="" label=""></option>
								{
									Object.keys(Activity).filter(x => !(parseInt(x) >= 0)).map(x => {
										return <option key={`activity${x}`} value={x}>{l10n.preliminary_activity[x as ActivityKeys]}</option>
									})
								}
								<option value={NoAnswer}>{l10n.preliminary_form_answer_noAnswer}</option>
							</Form.Control>
							{
								preliminary.activity?.value === Activity[Activity.other] &&
								<Form.Group as={Form.Row} className='pt-2 pl-1' size='sm'>
									<Form.Label xs='auto' column={true}>
										{l10n.preliminary_form_other_text}
									</Form.Label>
										<Col xs='12' sm={true}>
										<Form.Control as='input' type='text' name='activityOther' value={preliminary.activity?.other === undefined ? '' : preliminary.activity?.other} onChange={activityOtherChanged} />
									</Col>
								</Form.Group>
							}
						</Form.Group>

						<Form.Group controlId='education'>
							<Form.Label>{l10n.preliminary_education_title}</Form.Label>
							<Form.Control as='select' name='education' value={preliminary.education?.value} onChange={educationChanged}>
								<option hidden value="" label=""></option>
								{
									Object.keys(Education).filter(x => !(parseInt(x) >= 0)).map(x => {
										return <option key={`education${x}`} value={x}>{l10n.preliminary_education_choices[x as EducationKeys]}</option>
									})
								}
								<option value={NoAnswer}>{l10n.preliminary_form_answer_noAnswer}</option>
							</Form.Control>

							{
								preliminary.education?.value === Education[Education.other] &&
								<Form.Group as={Form.Row} className='pt-2 pl-1' size='sm'>
									<Form.Label xs='auto' column={true}>
										{l10n.preliminary_form_other_text}
									</Form.Label>
										<Col xs='12' sm={true}>
										<Form.Control as='input' type='text' name='educationOther' value={preliminary.education?.other === undefined ? '' : preliminary.education?.other} onChange={educationOtherChanged} />
									</Col>
								</Form.Group>
							}
						</Form.Group>
						<Form.Row className='d-flex justify-content-center my-4'>
							<Col xs='auto'>
								<Button onClick={() => navigateBack()}>{l10n.button_previous}</Button>
							</Col>
							<Col xs='auto' className='offset-1'>
								<Button disabled={context.preliminaryFilled === false} onClick={() => navigateNext()}>{l10n.button_next}</Button>
							</Col>
						</Form.Row>
					</Form>
				</Col>
			</Row>
		</Container>
	);
}